import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import infoIcon from "icons/info-new-rows.svg";
import PropTypes from "prop-types";
import { editUser } from "slices/userAdmin/actions/userAdmin";
import Checkbox, {
  checkboxStates,
} from "@visiblethread/react-components/dist/components/Checkbox";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import Field, { fieldTypes } from "components/Field";

const EditUserAndPermissionsModal = ({
  userName,
  currentFirstName,
  currentLastName,
  currentLicensed,
  currentUserAdmin,
  currentReadabilitySettingsAdmin,
  currentAcronymSettingsAdmin,
  currentDiscoverySettingsAdmin,
  currentExcelOutputsAdmin,
  currentDictionariesAdmin,
}) => {
  const dispatch = useDispatch();

  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [licensed, setLicensed] = useState(currentLicensed);
  const [userAdmin, setUserAdmin] = useState(currentUserAdmin);
  const [readabilitySettingsAdmin, setReadabilitySettingsAdmin] = useState(
    currentReadabilitySettingsAdmin
  );
  const [acronymSettingsAdmin, setAcronymSettingsAdmin] = useState(
    currentAcronymSettingsAdmin
  );
  const [discoverySettingsAdmin, setDiscoverySettingsAdmin] = useState(
    currentDiscoverySettingsAdmin
  );
  const [excelOutputsAdmin, setExcelOutputsAdmin] = useState(
    currentExcelOutputsAdmin
  );
  const [dictionariesAdmin, setDictionariesAdmin] = useState(
    currentDictionariesAdmin
  );

  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const isUserNamePassword = useSelector(
    (state) => state?.systemAdmin.isUsernamePassword
  );
  const userNamePassEnabled = () =>
    firstNameError === false &&
    lastNameError === false &&
    passwordError === "" &&
    confirmPasswordError === "";
  const ssoDisabled = () => firstNameError === false && lastNameError === false;
  const isOkButtonEnabled = () =>
    isUserNamePassword ? userNamePassEnabled() : ssoDisabled();

  const handleEditUser = () => {
    if (isOkButtonEnabled()) {
      dispatch(
        editUser(
          userName,
          firstName,
          lastName,
          password,
          licensed,
          userAdmin,
          readabilitySettingsAdmin,
          acronymSettingsAdmin,
          discoverySettingsAdmin,
          excelOutputsAdmin,
          dictionariesAdmin
        )
      );
    }
  };

  const onEnterKeyDown = ({ key }) => {
    if (key === "Enter" && isOkButtonEnabled()) {
      handleEditUser();
    }
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.EDIT_USER_AND_PERMISSIONS));
  };

  const userNameField = () => (
    <label
      htmlFor="edit-user-modal-user-name"
      className="edit__user__modal__username"
    >
      <b>User Name</b>
      <input
        type="text"
        id="edit-user-modal-user-name"
        value={userName}
        disabled
      />
    </label>
  );

  const modalBodyLicensed = () => (
    <div className="edit__permissions__modal__licensed">
      <div data-title="This allows the user create modify and delete users within the workspace.">
        <b>Is Licensed User</b>
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={licensed ? checkboxStates.checked : checkboxStates.empty}
          handleChange={() => {
            if (licensed) {
              setDictionariesAdmin(false);
              setReadabilitySettingsAdmin(false);
              setAcronymSettingsAdmin(false);
              setDiscoverySettingsAdmin(false);
              setExcelOutputsAdmin(false);
            }
            setLicensed(!licensed);
          }}
          disabled={false}
          id="cb-edit-permissions-licensed"
          value="edit-permissions-licensed-checkbox"
        />
      </div>
    </div>
  );
  const modalBodyDictionaries = () => (
    <div className="edit__permissions__modal__dictionaries">
      <div data-title="Making a user 'active' allows them sign in and work with documents. Only 'Active' users use a license.">
        Can Edit Dictionaries
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            dictionariesAdmin ? checkboxStates.checked : checkboxStates.empty
          }
          handleChange={() => {
            setDictionariesAdmin(!dictionariesAdmin);
          }}
          disabled={!licensed}
          id="cb-edit-permissions-dictionaries"
          value="edit-permissions-dictionaries-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyReadabilitySettings = () => (
    <div className="edit__permissions__modal__readability__settings">
      <div data-title="Enabling this permission allows this user edit the Readability Settings for this workspace.">
        Can Edit Readability Settings
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            readabilitySettingsAdmin
              ? checkboxStates.checked
              : checkboxStates.empty
          }
          handleChange={() => {
            setReadabilitySettingsAdmin(!readabilitySettingsAdmin);
          }}
          disabled={!licensed}
          id="cb-edit-permissions-readability-settings"
          value="edit-permissions-readability-settings-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyAcronymSettings = () => (
    <div className="edit__permissions__modal__acronym__settings">
      <div data-title="Enabling this permission allows this user edit the Acronym Settings for this workspace.">
        Can Edit Acronym Settings
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            acronymSettingsAdmin ? checkboxStates.checked : checkboxStates.empty
          }
          handleChange={() => {
            setAcronymSettingsAdmin(!acronymSettingsAdmin);
          }}
          disabled={!licensed}
          id="cb-edit-permissions-acronym-settings"
          value="edit-permissions-acronym-settings-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyDiscoverySettings = () => (
    <div className="edit__permissions__modal__discovery__settings">
      <div data-title="Enabling this permission allows this user edit the Discovery Settings for this workspace.">
        Can Edit Discovery Settings
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            discoverySettingsAdmin
              ? checkboxStates.checked
              : checkboxStates.empty
          }
          handleChange={() => {
            setDiscoverySettingsAdmin(!discoverySettingsAdmin);
          }}
          disabled={!licensed}
          id="cb-edit-permissions-discovery-settings"
          value="edit-permissions-discovery-settings-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyExcelOutputs = () => (
    <div className="edit__permissions__modal__excel__outputs">
      <div data-title="Enabling this permission allows this user edit the Excel Outputs for this workspace.">
        Can Edit Excel Outputs
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            excelOutputsAdmin ? checkboxStates.checked : checkboxStates.empty
          }
          handleChange={() => {
            setExcelOutputsAdmin(!excelOutputsAdmin);
          }}
          disabled={!licensed}
          id="cb-edit-permissions-excel__outputs"
          value="edit-permissions-excel__outputs-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyAdmin = () => (
    <div className="edit__permissions__modal__user__admin">
      <div data-title="Enabling this permission allows this user create and modify dictionaries.">
        <b>Can Administer Users</b>
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={userAdmin ? checkboxStates.checked : checkboxStates.empty}
          handleChange={() => {
            setUserAdmin(!userAdmin);
          }}
          disabled={false}
          id="cb-edit-permissions-user-admin"
          value="edit-permissions-user-admin-checkbox"
        />
      </div>
    </div>
  );

  return (
    <Modal
      data-test="editUserModal"
      className="fade-in"
      show
      onDismiss={handleCancel}
      backdrop="static"
      centered
      onKeyDown={onEnterKeyDown}
    >
      <Modal.Header closeButton>
        <b className="edit__user__modal__header">Edit User</b>
      </Modal.Header>
      <Modal.Body>
        <div className="edit__user__modal">
          {userNameField()}
          <Field
            type={fieldTypes.firstName}
            propsObject={{
              firstName,
              setFirstName,
              firstNameError,
              setFirstNameError,
            }}
          />
          <Field
            type={fieldTypes.lastName}
            propsObject={{
              lastName,
              setLastName,
              lastNameError,
              setLastNameError,
            }}
          />
          {isUserNamePassword && (
            <Field
              type={fieldTypes.passwordAndConfirm}
              propsObject={{
                password,
                setPassword,
                passwordError,
                setPasswordError,
                confirmPassword,
                setConfirmPassword,
                confirmPasswordError,
                setConfirmPasswordError,
              }}
            />
          )}
        </div>
        <div className="edit__permissions__modal">
          {modalBodyLicensed()}
          {modalBodyDictionaries()}
          {modalBodyReadabilitySettings()}
          {modalBodyAcronymSettings()}
          {modalBodyDiscoverySettings()}
          {modalBodyExcelOutputs()}
          {modalBodyAdmin()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          data-test="saveButton"
          variant={buttonVariants.primary}
          onClick={handleEditUser}
          disabled={!isOkButtonEnabled()}
          content="Save"
        />
        <Button
          data-test="cancelButton"
          variant={buttonVariants.secondary}
          onClick={handleCancel}
          content="Cancel"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserAndPermissionsModal;

EditUserAndPermissionsModal.propTypes = {
  userName: PropTypes.string.isRequired,
  currentFirstName: PropTypes.string.isRequired,
  currentLastName: PropTypes.string.isRequired,
  currentLicensed: PropTypes.bool.isRequired,
  currentUserAdmin: PropTypes.bool.isRequired,
  currentReadabilitySettingsAdmin: PropTypes.bool.isRequired,
  currentAcronymSettingsAdmin: PropTypes.bool.isRequired,
  currentDiscoverySettingsAdmin: PropTypes.bool.isRequired,
  currentExcelOutputsAdmin: PropTypes.bool.isRequired,
  currentDictionariesAdmin: PropTypes.bool.isRequired,
};
